import * as React from "react"
import { Checkbox } from "@workspace/ui/components/shadcn/checkbox"
import { Label } from "@workspace/ui/components/shadcn/label"
import { cn } from "@workspace/ui/lib/utils"
import type { FieldDef, FormApi } from "./fieldTypes"
import { normalizeInlineLabel, getRequiredMark, getHelpText, getErrorText } from "./fieldUtils"

type Props = {
    def: Extract<FieldDef, { type: "checkbox" }>
    form: FormApi
    invalid: boolean
}

function getFrameClass(def: FieldDef, invalid: boolean) {
    const isDense = (def as any).density === "dense" || (def as any).dense === true

    return cn(
        "ui-control",
        invalid && "ui-control-error",
        !invalid && "ui-control-focus",
        "w-full min-w-0 rounded-md px-3 text-base shadow-xs cursor-pointer select-none",
        "flex items-center gap-3",
        isDense ? "h-8" : "h-9",
        def.disabled && "ui-control-disabled"
    )
}

export function FieldCheckbox({ def, form, invalid }: Props) {
    const name = def.name
    const field = form.register(name)
    const value = form.getValue(name)
    const isChecked = !!value
    const text = normalizeInlineLabel(def.inlineLabel ?? "")
    const frameClass = getFrameClass(def, invalid)

    const requiredMark = getRequiredMark(def)
    const helpText = getHelpText(def)

    const err = (form.formState.errors as any)?.[def.name]
    const errorText = getErrorText(err)

    return (
        <>
            <div className="ui-field-header ">
                {def.label ? (
                    <Label
                        htmlFor={def.name}
                        className="ui-field-label"
                    >
                        {def.label}
                        {requiredMark}
                    </Label>
                ) : (
                    <span />
                )}

                {helpText ? (
                    <span className="ui-field-helptext">
                        {helpText}
                    </span>
                ) : (
                    <span />
                )}
            </div>

            <label className={frameClass}>
                <Checkbox
                    checked={isChecked}
                    disabled={def.disabled}
                    onCheckedChange={(checked) => field.onChange(checked)}
                    onBlur={field.onBlur}
                />

                <span className="min-w-0 text-xs whitespace-normal break-words">
                    {text}
                </span>
            </label>

            <div className="ui-field-error">
                {invalid ? errorText : null}
            </div>

        </>
    )
}
